import React from 'react'

import {
  BaseResponsiveDropdownProps,
  Checkbox,
  Dropdown,
  useResponsiveDropdown,
  WorkspaceLogo,
} from '@sketch/components'
import { useThemeContext } from '@sketch/global-styles'
import { WorkspaceMinimalFragment } from '@sketch/gql-types'

import {
  InboxSearchOptions,
  useInboxSearchContext,
} from '../../views/Inbox/InboxSearchContext'

import {
  CheckboxList,
  DateInputs,
  DatePicker,
  Section,
  SectionHeader,
  TriggerChevron,
  WorkspaceIcon,
  WorkspaceMenu,
  WorkspaceOptionLabel,
  WorkspaceTrigger,
  WorkspaceTriggerContent,
  Wrapper,
} from './InboxFilters.styles'

type NotificationTypeKey =
  | 'billing'
  | 'comments'
  | 'replies'
  | 'mentions'
  | 'downloadableAssets'
  | 'starredUpdates'

const NOTIFICATION_TYPE_OPTIONS: { key: NotificationTypeKey; label: string }[] =
  [
    { key: 'billing', label: 'Billing' },
    { key: 'comments', label: 'Comments' },
    { key: 'replies', label: 'Replies' },
    { key: 'mentions', label: 'Mentions' },
    { key: 'downloadableAssets', label: 'Downloadable Assets' },
    { key: 'starredUpdates', label: 'Starred Updates' },
  ]

interface WorkspaceDropdownProps extends BaseResponsiveDropdownProps {
  workspaces: WorkspaceMinimalFragment[]
  selectedWorkspaceId: string | null
  onSelect: (workspaceId: string | null) => void
}

const WorkspaceDropdown = ({
  workspaces,
  selectedWorkspaceId,
  onSelect,
}: WorkspaceDropdownProps) => (
  <WorkspaceMenu>
    <Dropdown.Item
      onClick={() => onSelect(null)}
      aria-selected={selectedWorkspaceId === null}
    >
      <WorkspaceOptionLabel>
        <WorkspaceIcon />
        <span>All Workspaces</span>
      </WorkspaceOptionLabel>
    </Dropdown.Item>
    {workspaces.map(workspace => (
      <Dropdown.Item
        key={workspace.identifier}
        onClick={() => onSelect(workspace.identifier)}
        aria-selected={selectedWorkspaceId === workspace.identifier}
      >
        <WorkspaceOptionLabel>
          <WorkspaceLogo
            workspaceName={workspace.name}
            src={workspace.avatar?.small}
            size="17px"
          />
          <span>{workspace.name}</span>
        </WorkspaceOptionLabel>
      </Dropdown.Item>
    ))}
  </WorkspaceMenu>
)

interface WorkspaceSelectorProps {
  workspaces: WorkspaceMinimalFragment[]
}

const WorkspaceSelector = ({ workspaces }: WorkspaceSelectorProps) => {
  const { options, setOptions } = useInboxSearchContext()

  const handleSelect = (workspaceId: string | null) =>
    setOptions({ ...options, workspace: workspaceId })

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: WorkspaceDropdown,
    dropdownProps: {
      workspaces,
      selectedWorkspaceId: options.workspace,
      onSelect: handleSelect,
    },
    placement: 'bottom-start',
  })

  const selected = options.workspace
    ? workspaces.find(w => w.identifier === options.workspace)
    : null

  return (
    <>
      <WorkspaceTrigger {...buttonProps} aria-label="From workspace">
        <WorkspaceTriggerContent>
          {selected ? (
            <>
              <WorkspaceLogo
                workspaceName={selected.name}
                src={selected.avatar?.small}
                size="17px"
              />
              <span>{selected.name}</span>
            </>
          ) : (
            <>
              <WorkspaceIcon />
              <span>All Workspaces</span>
            </>
          )}
        </WorkspaceTriggerContent>
        <TriggerChevron />
      </WorkspaceTrigger>
      {content}
    </>
  )
}

interface InboxFiltersProps {
  workspaces?: WorkspaceMinimalFragment[]
}

export const InboxFilters = ({ workspaces = [] }: InboxFiltersProps) => {
  const { options, setOptions } = useInboxSearchContext()
  const { isDarkMode } = useThemeContext()

  const toggle = (key: keyof InboxSearchOptions) => () =>
    setOptions({ ...options, [key]: !options[key] })

  const handleDateUpdate = (
    e: React.ChangeEvent<HTMLInputElement>,
    dateType: 'from' | 'to'
  ) => setOptions({ ...options, [dateType]: e.target.value })

  return (
    <Wrapper aria-label="Inbox filters">
      <Section>
        <SectionHeader>From</SectionHeader>
        <WorkspaceSelector workspaces={workspaces} />
      </Section>

      <Section>
        <SectionHeader>Show only</SectionHeader>
        <CheckboxList>
          {NOTIFICATION_TYPE_OPTIONS.map(({ key, label }) => (
            <Checkbox
              key={key}
              value={options[key] ? 1 : 0}
              name={key}
              checked={options[key]}
              label={label}
              onChange={toggle(key)}
            />
          ))}
        </CheckboxList>
      </Section>

      <Section>
        <SectionHeader>Between</SectionHeader>
        <DateInputs>
          <DatePicker
            placeholder="dd/mm/yyyy"
            aria-label="from"
            isDarkMode={!!isDarkMode}
            value={options.from}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleDateUpdate(e, 'from')
            }
            max={options.to || new Date().toLocaleDateString('en-CA')}
          />
          <DatePicker
            placeholder="dd/mm/yyyy"
            aria-label="to"
            isDarkMode={!!isDarkMode}
            value={options.to}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleDateUpdate(e, 'to')
            }
            min={options.from}
            max={new Date().toLocaleDateString('en-CA')}
          />
        </DateInputs>
      </Section>
    </Wrapper>
  )
}
